//@ts-nocheck
/**
 * src/layouts/ImmersiveLayout.tsx
 * ---------------------------------------------------------------------------
 * Dedicated immersive layout for Unity WebGL scene routes.
 *
 * This layout renders ONLY the Unity scene — no navbar, no footer,
 * no HomeLayout wrappers, no website chrome. The experience occupies
 * the full viewport (100vw × 100vh) for a standalone XR application feel.
 *
 * Handles:
 *  - Scene data fetching via useGetSceneDetail
 *  - Product/teleportation/video/annotation data assembly
 *  - UnityRenderer rendering
 *  - web_url iframe fallback
 *  - Body scroll lock
 *
 * Does NOT render:
 *  - Navbar
 *  - Footer
 *  - Floating social menus
 *  - Landing page content
 *  - Breadcrumbs
 */
import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetSceneDetail } from "../api/scenes";
import { useGetConfig } from "../api/config";
import { useGetBasicSettingsList } from "../api/settings";
import { UnityRenderer } from "../Components/public/Loaders";
import { Head } from "../Components/public/Navbar";
import WebUrlIframe from "../Components/public/WebUrlIframe/WebUrlIframe";
import TourViewer, {
  getTourIdFromScene,
  is360TourScene,
  TourLoadOverlay,
} from "../Components/public/TourViewer";

import "./ImmersiveLayout.css";

const getWebUrlSrc = webUrl => {
  if (!webUrl) return "";
  if (typeof webUrl === "object") return webUrl.url || "";
  return typeof webUrl === "string" ? webUrl : "";
};

export default function ImmersiveLayout() {
  const { teleportationId } = useParams();

  // ── API data ────────────────────────────────────────────────────────────
  const {
    data: config,
    isLoading: isLoadingConfig,
    isSuccess: isSuccessConfig,
  } = useGetConfig();

  const {
    data: sceneDetailApi,
    isLoading,
    isSuccess,
  } = useGetSceneDetail(teleportationId || null);

  const { data: basicSettingList, isLoading: basicSettingLoading } =
    useGetBasicSettingsList();

  // ── Derived scene data (pure memoized calculation, no render-phase setState) ──
  const {
    productArray,
    overlayProduct,
    videoArray,
    telportationArray,
    annotationObject,
  } = useMemo(() => {
    if (isLoading || !sceneDetailApi) {
      return {
        productArray: null,
        overlayProduct: null,
        videoArray: null,
        telportationArray: null,
        annotationObject: null,
      };
    }
    try {
      const _productArray: Record<string, any> = {};
      const _overlayProduct: any[] = [];
      sceneDetailApi.product_categories?.forEach((item: any) => {
        if (!item.product_button_id) {
          _overlayProduct.push(item);
        }
        _productArray[item.id] = item.product_button_id;
      });

      const _telportationArray: any[] = [];
      const _annotationArray: any[] = [];
      const _videoArray: Record<string, any> = {};
      sceneDetailApi.interactions?.forEach((item: any) => {
        switch (item.action_type) {
          case "Teleportation":
            _telportationArray.push({
              teleportId: item.unity_scene_teleportation_id,
              teleportLink: `/scene/${item.scene}`,
            });
            break;
          case "Video":
            _videoArray[item.unity_scene_video_id] = item.embed_link;
            break;
          case "Annotation":
            _annotationArray.push({
              annotation_id: item.unity_scene_annotation_id,
              action_number: 1,
              title: item.name,
              text: item.name,
            });
            break;
          default:
            break;
        }
      });

      return {
        productArray: _productArray,
        overlayProduct: _overlayProduct,
        videoArray: _videoArray,
        telportationArray: _telportationArray,
        annotationObject: { annotations: _annotationArray },
      };
    } catch (error) {
      console.log("ImmersiveLayout: error assembling scene data", error);
      return {
        productArray: null,
        overlayProduct: null,
        videoArray: null,
        telportationArray: null,
        annotationObject: null,
      };
    }
  }, [isLoading, sceneDetailApi]);

  // ── Lock body scroll while immersive ──────────────────────────────────
  useEffect(() => {
    const html = document.documentElement;
    const root = document.getElementById("root");
    const original = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: document.body.style.overflow,
      bodyHeight: document.body.style.height,
      bodyWidth: document.body.style.width,
      bodyOverscroll: document.body.style.overscrollBehavior,
      rootOverflow: root?.style.overflow,
      rootHeight: root?.style.height,
    };

    html.style.overflow = "hidden";
    html.style.height = "100%";
    document.body.style.overflow = "hidden";
    document.body.style.height = "100%";
    document.body.style.width = "100%";
    document.body.style.overscrollBehavior = "none";
    if (root) {
      root.style.overflow = "hidden";
      root.style.height = "100%";
    }

    return () => {
      html.style.overflow = original.htmlOverflow;
      html.style.height = original.htmlHeight;
      document.body.style.overflow = original.bodyOverflow;
      document.body.style.height = original.bodyHeight;
      document.body.style.width = original.bodyWidth;
      document.body.style.overscrollBehavior = original.bodyOverscroll;
      if (root) {
        root.style.overflow = original.rootOverflow;
        root.style.height = original.rootHeight;
      }
    };
  }, []);

  // ── Loading state ─────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="immersive-layout">
        <Head title={config?.data?.title} favicon={config?.data?.favicon} />
        <TourLoadOverlay visible label="Loading..." />
      </div>
    );
  }

  // ── 360 tour viewer ───────────────────────────────────────────────────
  if (is360TourScene(sceneDetailApi)) {
    return (
      <div className="immersive-layout">
        <Head title={config?.data?.title} favicon={config?.data?.favicon} />
        <div className="immersive-layout__scene">
          <TourViewer
            tourId={getTourIdFromScene(sceneDetailApi)}
            initialTour={
              sceneDetailApi?.tour_360 && typeof sceneDetailApi.tour_360 === "object"
                ? sceneDetailApi.tour_360
                : null
            }
          />
        </div>
      </div>
    );
  }

  if (isLoadingConfig) {
    return (
      <div className="immersive-layout">
        <Head title={config?.data?.title} favicon={config?.data?.favicon} />
        <TourLoadOverlay visible label="Loading..." />
      </div>
    );
  }

  const webUrlSrc = getWebUrlSrc(sceneDetailApi?.web_url);

  // ── web_url iframe fallback (e.g. Vagon streaming) ────────────────────
  if (webUrlSrc && !isLoadingConfig) {
    return (
      <div className="immersive-layout">
        <Head title={config?.data?.title} favicon={config?.data?.favicon} />
        <div className="immersive-layout__scene">
          <WebUrlIframe
            src={webUrlSrc}
            className="immersive-layout__iframe"
          />
        </div>
      </div>
    );
  }

  // ── Main Unity scene ──────────────────────────────────────────────────
  return (
    <div className="immersive-layout">
      <Head title={config?.data?.title} favicon={config?.data?.favicon} />
      <div className="immersive-layout__scene">
        {!isLoading && !isLoadingConfig && sceneDetailApi?.Unity_Scene?.name && (
          <UnityRenderer
            tabIndex={1}
            fileName={sceneDetailApi.Unity_Scene.name}
            fileUrl={sceneDetailApi.Unity_Scene.unity_file}
            loadingText={
              sceneDetailApi?.Unity_Scene?.loading_text
                ? sceneDetailApi.Unity_Scene.loading_text
                : basicSettingList?.default_loading_text
            }
            loadingImageBg={sceneDetailApi?.Unity_Scene?.background_image}
            loadingImage={sceneDetailApi?.image}
            productPanel={productArray}
            teleportation={telportationArray}
            video={videoArray}
            annotations={annotationObject}
            basicSettingList={basicSettingList}
            basicSettingLoading={basicSettingLoading}
            sceneDetailApi={sceneDetailApi}
            productCategories={sceneDetailApi?.product_categories}
            overlayProduct={overlayProduct}
            cmsMetadata={false}
          />
        )}
        {!isLoading && !sceneDetailApi?.Unity_Scene?.name && !webUrlSrc && (
          <div className="immersive-layout immersive-layout--empty">
            <p>No scene data available.</p>
          </div>
        )}
      </div>
    </div>
  );
}
